import {SimpleGrid} from '@mantine/core';
import React from 'react';

import DataSharingWarningBanner from 'analysis-tool/ui/components/DataSharingWarningBanner';
import BenchlingConfig from 'BenchlingConfig';
import FolderSelector, {DEFAULT_PROJECT_FOLDER_LABEL_TEXT} from 'file-browser/FolderSelector';
import StudySearchInput, {StudySearchItem} from 'studies/StudySearchInput';
import FormGroup from 'ui-library/bs/FormGroup';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import TextField from 'ui-library/inputs/TextField';

import SeedDataRender from './SeedDataRender';
import {SeedDataConfig} from './SeedDataTypes';
import {CreateAnalysisState} from './useCreateAnalysisState';

export interface CreateAnalysisFormProps {
  /**
   * State for configuring how to create an analysis. See `useCreateAnalysisState`.
   */
  createAnalysisState: CreateAnalysisState;
  /**
   * See `useCreateAnalysisState`.
   */
  setCreateAnalysisState: (newState: CreateAnalysisState) => void;
  /**
   * Prevent users from editing the studies to which the analysis will be added.
   *
   * See `CreateAnalysisStateDefaults.initialStudyItems` for how to set the default (and uneditable)
   * study.
   *
   * Note: has no effect when `showStudiesField` is false.
   */
  disableEditStudies?: boolean;
  /**
   * Custom info banner.
   */
  headerInlineBanner?: JSX.Element;
  /**
   * Hide the banner that warns that pulling data into an analysis will expose the data to all users with
   * access to the same folder.
   */
  hideDataSharingWarningBanner?: boolean;
  /** Whether to show the Studies field. Defaults to BenchlingConfig.STUDIES_ENABLE */
  showStudiesField?: boolean;
}

/**
 * Form for configuring how to create an analysis. This is the contents of `CreateAnalysisModal`.
 *
 * `CreateAnalysisForm` should be used in conjunction with `useCreateAnalysisState`:
 *
 *    const {createAnalysisState, setCreateAnalysisState, requestParamsInfo} = useCreateAnalysisState({
 *      initialAnalysisName: 'New Analysis'
 *    });
 *    return (
 *      <>
 *        <CreateAnalysisForm
 *          createAnalysisState={createAnalysisState}
 *          setCreateAnalysisState={setCreateAnalysisState}
 *        />
 *        <Button
 *          disabled={!requestParamsInfo.isComplete}
 *          onClick={() => AnalysisDAO.createAnalysis(requestParamsInfo.requestParams)}
 *        />
 *      </>
 *    );
 */
export default function CreateAnalysisForm({
  createAnalysisState,
  setCreateAnalysisState,
  disableEditStudies,
  headerInlineBanner,
  hideDataSharingWarningBanner,
  showStudiesField = BenchlingConfig.STUDIES_ENABLE,
}: CreateAnalysisFormProps): JSX.Element {
  const showAnalysisTableNameField = createAnalysisState.analysisTableName != null;

  return (
    <>
      {headerInlineBanner}
      <SimpleGrid cols={2} spacing="md" breakpoints={[{maxWidth: 'xs', cols: 1}]}>
        <FormGroup>
          <TextField
            label={showAnalysisTableNameField ? 'Analysis name' : 'Name'}
            placeholder="Enter a name for this analysis"
            autoFocus={true}
            value={createAnalysisState.analysisName}
            onChange={(e: {target: {value: string}}) => {
              setCreateAnalysisState({...createAnalysisState, analysisName: e.target.value});
            }}
            dataTestAttrs={{component: 'CreateAnalysisModal', element: 'nameTextField'}}
            isRequired={true}
          />
        </FormGroup>
        <FormGroup>
          <FieldLabel label={DEFAULT_PROJECT_FOLDER_LABEL_TEXT} isRequired={true} />
          <FolderSelector
            enableRedesign={true}
            showNotebook={true}
            value={createAnalysisState.folderId}
            onChange={(e: {target: {value: string}}) => {
              setCreateAnalysisState({...createAnalysisState, folderId: e.target.value});
            }}
            unsetOnFetchFail={true}
          />
        </FormGroup>
      </SimpleGrid>
      {showAnalysisTableNameField && (
        <FormGroup>
          <TextField
            label="Analysis table name"
            autoFocus={false}
            value={createAnalysisState.analysisTableName}
            onChange={(e: {target: {value: string}}) => {
              setCreateAnalysisState({...createAnalysisState, analysisTableName: e.target.value});
            }}
            dataTestAttrs={{component: 'CreateAnalysisModal', element: 'dataFrameTextField'}}
          />
        </FormGroup>
      )}
      {showStudiesField && (
        <FormGroup>
          <FieldLabel label="Studies" />
          <StudySearchInput
            onChange={(studyItems: Array<StudySearchItem>) => {
              setCreateAnalysisState({...createAnalysisState, studyItems});
            }}
            shouldAllowMultiple={true}
            selectedItems={createAnalysisState.studyItems}
            isReadOnly={disableEditStudies ?? false}
            hasNonInteractiveChips={true}
          />
        </FormGroup>
      )}
      {createAnalysisState.seedDataConfig && (
        <SeedDataRender
          seedDataConfig={createAnalysisState.seedDataConfig}
          setSeedDataConfig={(seedDataConfig: SeedDataConfig) => {
            setCreateAnalysisState({...createAnalysisState, seedDataConfig});
          }}
        />
      )}
      {!hideDataSharingWarningBanner && <DataSharingWarningBanner folderId={createAnalysisState.folderId} />}
    </>
  );
}
